/**
 * Wire schemas for the Operations Intake Verification surface.
 *
 * These are the on-the-wire contracts between worker and web app —
 * one schema per endpoint payload. The web app parses with these on
 * receive; the worker validates with these before sending. Anything
 * the UI renders must be present here.
 *
 * Spec: engineering/command-center/docs/port-notes/intake-verification.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";
import { LeadSchema } from "./lead.js";

/**
 * One Handling Firm a lead was routed to — the firm name (joined from
 * Firms by the composer) and the placement's current decision.
 */
export const LeadRoutingPlacementSchema = z.object({
  /** Handling Firm name, or null when the firm lookup missed. */
  firmName: z.string().nullable(),
  /** Placement Decision (Sent, Reviewing, Accepted, …), or null. */
  decision: z.string().nullable(),
});
export type LeadRoutingPlacement = z.infer<typeof LeadRoutingPlacementSchema>;

/**
 * "Did it move, and if so where?" — derived per lead by the composer,
 * not stored. Lead Status + the lead's placements are the truth.
 *
 *   moved   — has a placement; `placements` lists the firm(s) it went to.
 *   stuck   — no placement; `stuckReason` says why (from Lead Status):
 *             `unmatched` (no matching firm → decline) or `incomplete`
 *             (missing info → get info).
 *   pending — no placement, just arrived (Unreviewed); routing hasn't run.
 */
export const LeadRoutingSchema = z.object({
  state: z.enum(["moved", "stuck", "pending"]),
  /** Firms routed to. Non-empty only when state === "moved". */
  placements: z.array(LeadRoutingPlacementSchema),
  /** Why it's stuck. Set only when state === "stuck". */
  stuckReason: z.enum(["unmatched", "incomplete"]).nullable(),
});
export type LeadRouting = z.infer<typeof LeadRoutingSchema>;

/**
 * One Lead projected for the surface — the typed Lead plus the
 * resolved OF name and the derived routing state. Both joins are done
 * by the composition service so the UI doesn't do its own lookups.
 */
export const IntakeLeadSchema = LeadSchema.extend({
  /** Joined `Firm Name` for `originatingFirmId`, or null if no match
   *  (the parser cleared the OF) or the Firms lookup missed. */
  originatingFirmName: z.string().nullable(),
  /** Did it route, and where — derived from Lead Status + placements. */
  routing: LeadRoutingSchema,
});
export type IntakeLead = z.infer<typeof IntakeLeadSchema>;

/**
 * One email's worth of work — all leads parsed from the same source
 * email, grouped by (senderEmail, time bucket). The verify action
 * operates at this granularity: all leads in a group flip together.
 */
export const IntakeEmailGroupSchema = z.object({
  /** Stable derived key — sha256(sender + ":" + bucket). Used as
   *  the URL parameter on POST /verify. */
  id: z.string(),
  /** Raw sender email of the group. Null when the parser did not
   *  capture a sender — those records become single-lead groups
   *  keyed off their Airtable record id. */
  senderEmail: z.string().nullable(),
  /** OF name for the group — taken from the first lead's
   *  `originatingFirmName`. Null when the OF is unknown. */
  senderName: z.string().nullable(),
  /** Earliest `createdTime` of the leads in this group (ISO-8601).
   *  Drives the "time received" column. */
  receivedAt: z.string(),
  leads: z.array(IntakeLeadSchema).min(1),
});
export type IntakeEmailGroup = z.infer<typeof IntakeEmailGroupSchema>;

/**
 * Payload shape for `GET /api/intake-verification`. The list is
 * already filtered to unverified groups, sorted newest first.
 */
export const IntakeQueueResponseSchema = z.object({
  groups: z.array(IntakeEmailGroupSchema),
});
export type IntakeQueueResponse = z.infer<typeof IntakeQueueResponseSchema>;

/**
 * Payload shape for `POST /api/intake-verification/:groupId/verify`.
 * No body required from the client; the server uses the URL param +
 * the authenticated user identity.
 */
export const IntakeVerifyResponseSchema = z.object({
  ok: z.literal(true),
});
export type IntakeVerifyResponse = z.infer<typeof IntakeVerifyResponseSchema>;
